import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { alpha } from '@mui/material/styles';
import { fetchAdmin, toErrorMessage } from '../../../admin/api';
import { PATH_DASHBOARD } from '../../../routes/paths';
import {
  AdminActionBar,
  AdminNoDataState,
  AdminScreenLayout,
  AdminStatusChip,
} from '../shared';
import SosSignalDetailsDrawer from './SosSignalDetailsDrawer';
import { formatDateTime, formatValue, getSignalFlags } from './sosShared';

const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const TABLE_MAX_HEIGHT = 'calc(100vh - 360px)';

function buildSignalsRequest(statusFilter, page, pageSize) {
  if (statusFilter === 'active') {
    return {
      path: '/sos/active',
      params: { page, page_size: pageSize },
    };
  }

  const params = { page, page_size: pageSize };

  if (statusFilter === 'resolved') {
    params.status = 'resolved';
  }

  return { path: '/sos', params };
}

function normalizeSignalsResponse(response) {
  const signals = response?.signals || {};
  return {
    rows: Array.isArray(signals.data) ? signals.data : [],
    totalSize: Number(signals.total_size || 0),
  };
}

function OverviewSummaryCard({ title, value, caption, tone = 'default' }) {
  const colorMap = {
    default: 'text.primary',
    success: 'success.main',
    warning: 'warning.main',
    error: 'error.main',
  };

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Stack spacing={1}>
          <Typography variant="overline" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h4" color={colorMap[tone]}>
            {formatValue(value, '0')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {caption}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

OverviewSummaryCard.propTypes = {
  caption: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  tone: PropTypes.oneOf(['default', 'success', 'warning', 'error']),
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default function SosSignalsPageView() {
  const [statusFilter, setStatusFilter] = useState('active');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [listState, setListState] = useState({
    loading: false,
    error: null,
    data: normalizeSignalsResponse({}),
  });
  const [selectedSignalId, setSelectedSignalId] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);

  const loadSignals = useCallback(async () => {
    setListState((prev) => ({ ...prev, loading: true, error: null }));

    const request = buildSignalsRequest(statusFilter, page, pageSize);

    try {
      const response = await fetchAdmin(request.path, request.params);
      setListState({
        loading: false,
        error: null,
        data: normalizeSignalsResponse(response),
      });
    } catch (error) {
      setListState((prev) => ({
        ...prev,
        loading: false,
        error: toErrorMessage(error),
      }));
    }
  }, [page, pageSize, statusFilter]);

  useEffect(() => {
    loadSignals();
  }, [loadSignals]);

  const rows = listState.data.rows || [];

  return (
    <AdminScreenLayout
      title="SOS Signals"
      breadcrumbs={[
        { name: 'Admin', href: PATH_DASHBOARD.root },
        { name: 'SOS' },
        { name: 'Signals' },
      ]}
    >
      <AdminActionBar
        title="SOS Signal Queue"
        description="Review active, resolved, or all incidents from one list without losing context."
        actions={[
          {
            label: 'Refresh',
            variant: 'contained',
            icon: 'solar:refresh-bold',
          },
        ]}
        onActionClick={loadSignals}
      />

      <Card variant="outlined" sx={{ overflow: 'hidden' }}>
        <CardContent sx={{ pb: 0 }}>
          <Stack spacing={2.5}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
            >
              <Stack spacing={0.5}>
                <Typography variant="subtitle1">Signals</Typography>
                <Typography variant="body2" color="text.secondary">
                  Click a row or use the detail icon to review the incident in the drawer.
                </Typography>
              </Stack>

              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel id="sos-status-filter-label">Signal state</InputLabel>
                <Select
                  labelId="sos-status-filter-label"
                  label="Signal state"
                  value={statusFilter}
                  onChange={(event) => {
                    setStatusFilter(event.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="active">Active signals</MenuItem>
                  <MenuItem value="resolved">Resolved signals</MenuItem>
                  <MenuItem value="all">All signals</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            {listState.error ? (
              <Alert severity="error" variant="outlined">
                {listState.error}
              </Alert>
            ) : null}
          </Stack>
        </CardContent>

        {listState.loading ? (
          <Box sx={{ px: 3, pb: 3 }}>
            <Stack spacing={2}>
              <Skeleton variant="rounded" height={52} />
              <Skeleton variant="rounded" height={420} />
            </Stack>
          </Box>
        ) : null}

        {!listState.loading && !rows.length ? (
          <Box sx={{ px: 3, pb: 3 }}>
            <AdminNoDataState
              title={
                statusFilter === 'active'
                  ? 'No active SOS signals right now'
                  : statusFilter === 'resolved'
                    ? 'No resolved SOS incidents found'
                    : 'No SOS signals found'
              }
              description="No SOS signals matched the current filter."
            />
          </Box>
        ) : null}

        {!listState.loading && rows.length ? (
          <>
            <TableContainer
              sx={{
                maxHeight: TABLE_MAX_HEIGHT,
                overflow: 'auto',
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Table stickyHeader size="small" aria-label="SOS signals table">
                <TableHead>
                  <TableRow>
                    {[
                      'Signal ID',
                      'User ID',
                      'Status',
                      'Attempts',
                      'Distress',
                      'Case',
                      'Last Attempt',
                      'Block Expires',
                      'Created',
                    ].map((label) => (
                      <TableCell
                        key={label}
                        sx={{
                          fontWeight: 700,
                          bgcolor: 'background.paper',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.map((row) => {
                    const flags = getSignalFlags(row);
                    const isHighRisk = Number(row.distress_level || 0) >= 3;

                    return (
                      <TableRow
                        hover
                        key={row.id}
                        selected={selectedSignalId === row.id}
                        onClick={() => {
                          setSelectedSignalId(row.id);
                          setSelectedAction(null);
                        }}
                        sx={{
                          cursor: 'pointer',
                          '& td': { py: 1.1 },
                          backgroundColor: isHighRisk
                            ? alpha('#d32f2f', 0.04)
                            : 'transparent',
                          '&:hover': {
                            backgroundColor: isHighRisk
                              ? alpha('#d32f2f', 0.08)
                              : 'action.hover',
                          },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                          #{row.id}
                        </TableCell>

                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {formatValue(row.user_id)}
                        </TableCell>

                        <TableCell sx={{ minWidth: 240 }}>
                          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <AdminStatusChip status={formatValue(row.status, 'unknown')} />
                            {flags.isEscalatedToBroadcast ? (
                              <Chip label="Broadcasted" size="small" color="error" variant="outlined" />
                            ) : null}
                            {flags.hasLinkedCase ? (
                              <Chip label="Case Open" size="small" color="warning" variant="outlined" />
                            ) : null}
                            {flags.hasBlock ? (
                              <Chip label="Blocked" size="small" color="warning" variant="outlined" />
                            ) : null}
                          </Stack>
                        </TableCell>

                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {formatValue(row.attempt_count, '0')}
                        </TableCell>

                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {formatValue(row.distress_level, '0')}
                        </TableCell>

                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {formatValue(row.case_id, 'No case')}
                        </TableCell>

                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {formatDateTime(row.last_attempt_at)}
                        </TableCell>

                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {formatDateTime(row.block_expires_at, 'No active block')}
                        </TableCell>

                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {formatDateTime(row.created_at)}
                        </TableCell>

                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={listState.data.totalSize}
              page={Math.max(0, page - 1)}
              onPageChange={(_, nextPage) => setPage(nextPage + 1)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
              rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            />
          </>
        ) : null}
      </Card>

      <SosSignalDetailsDrawer
        open={Boolean(selectedSignalId)}
        signalId={selectedSignalId}
        initialAction={selectedAction}
        onInitialActionHandled={() => setSelectedAction(null)}
        onClose={() => {
          setSelectedSignalId(null);
          setSelectedAction(null);
        }}
        onSignalUpdated={loadSignals}
      />
    </AdminScreenLayout>
  );
}